import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Players from './pages/Players';
import Groups from './pages/Groups';
import Teams from './pages/Teams';
import Pairs from './pages/Pairs';
import Course from './pages/Course';
import ScoreEntry from './pages/ScoreEntry';
import ChangePassword from './pages/ChangePassword';
import Results from './pages/Results';
import Leaderboard from './pages/Leaderboard';
import Archive from './pages/Archive';
import Manual from './pages/Manual';
import ReleaseNotes from './pages/ReleaseNotes';
import WhsOverview from './pages/WhsOverview';
import PlayerStats from './pages/PlayerStats';
import PrintPlayerStats from './pages/PrintPlayerStats';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TwosAndFines from './pages/TwosAndFines';
import PlayerLogin from './pages/PlayerLogin';
import PlayerRegister from './pages/PlayerRegister';
import PlayerDashboard from './pages/PlayerDashboard';
import PlayerVerify from './pages/PlayerVerify';
import PlayerHcpVerify from './pages/PlayerHcpVerify';
import PlayerDeclareMarker from './pages/PlayerDeclareMarker';
import PlayerChangePassword from './pages/PlayerChangePassword';
import PlayerMyResults from './pages/PlayerMyResults';
import PlayerForgotPassword from './pages/PlayerForgotPassword';
import PlayerResetPassword from './pages/PlayerResetPassword';
import WinnersAndLosers from './pages/WinnersAndLosers';
import MultiArchive from './pages/MultiArchive';
import VerifyCard from './pages/VerifyCard';
import ChooseLogin from './pages/ChooseLogin';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import LicenceDashboard from './pages/LicenceDashboard';
import TuesdayGolf from './pages/TuesdayGolf';
import VoteResults from './pages/VoteResults';
import LinkAccounts from './pages/LinkAccounts';
import Settings from './pages/Settings';
import Finance from './pages/Finance';
import GeneralPlay from './pages/GeneralPlay';
import EventAttendance from './pages/EventAttendance';
import MailMerge from './pages/MailMerge';
import Planner from './pages/Planner';
import PlannerEventDetail from './pages/PlannerEventDetail';
import PlannerSignup from './pages/PlannerSignup';
import Terminal from './pages/Terminal';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DataProcessingAgreement from './pages/DataProcessingAgreement';
import CookieConsent from './components/CookieConsent';
import storage from './nativeStorage';

function PrivateRoute({ children }) {
  const token = storage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  localStorage.setItem('lastPage', window.location.pathname);
  return children;
}

function SuperAdminRoute({ children }) {
  const token = storage.getItem('token');
  const sa = storage.getItem('superadmin');
  if (!token || !sa) return <Navigate to="/superadmin" />;
  return children;
}

const isElectron = typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("electron");

function NotYetOffline({ feature }) {
  return (
    <Layout>
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🌐</div>
        <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px", color: "#14532d" }}>{feature} isn't available in the desktop app yet</div>
        <div style={{ fontSize: "14px", color: "#6b7280", maxWidth: "420px", margin: "0 auto" }}>This feature currently needs an internet connection — use the website version at golfsocietymanager.com instead.</div>
      </div>
    </Layout>
  );
}

function OfflineAware({ feature, children }) {
  return isElectron ? <NotYetOffline feature={feature} /> : children;
}

function App() {
  return (
    <Router>
      {!window.location.pathname.startsWith('/print/') && <CookieConsent />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/choose-login" element={<ChooseLogin />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/data-processing-agreement" element={<DataProcessingAgreement />} />
        <Route path="/print/player-stats/:name" element={<PrintPlayerStats />} />
        <Route path="/superadmin" element={<SuperAdminLogin />} />
        <Route path="/superadmin/dashboard" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
        <Route path="/superadmin/licences" element={<SuperAdminRoute><LicenceDashboard /></SuperAdminRoute>} />
        <Route path="/players" element={<PrivateRoute><Players /></PrivateRoute>} />
        <Route path="/groups" element={<PrivateRoute><Groups /></PrivateRoute>} />
        <Route path="/teams" element={<PrivateRoute><Teams /></PrivateRoute>} />
        <Route path="/pairs" element={<PrivateRoute><Pairs /></PrivateRoute>} />
        <Route path="/course" element={<PrivateRoute><Course /></PrivateRoute>} />
        <Route path="/scores" element={<PrivateRoute><ScoreEntry /></PrivateRoute>} />
        <Route path="/change-password" element={<PrivateRoute><OfflineAware feature="Change Password"><ChangePassword /></OfflineAware></PrivateRoute>} />
        <Route path="/results" element={<PrivateRoute><Results /></PrivateRoute>} />
        <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
        <Route path="/archive" element={<PrivateRoute><Archive /></PrivateRoute>} />
        <Route path="/manual" element={<PrivateRoute><Manual /></PrivateRoute>} />
        <Route path="/release-notes" element={<PrivateRoute><ReleaseNotes /></PrivateRoute>} />
        <Route path="/whs-overview" element={<PrivateRoute><WhsOverview /></PrivateRoute>} />
        <Route path="/stats" element={<PrivateRoute><PlayerStats /></PrivateRoute>} />
        <Route path="/mailmerge" element={<PrivateRoute><MailMerge /></PrivateRoute>} />
        <Route path="/planner" element={<PrivateRoute><Planner /></PrivateRoute>} />
        <Route path="/planner/:id" element={<PrivateRoute><PlannerEventDetail /></PrivateRoute>} />
        <Route path="/planner-signup/:slug" element={<PlannerSignup />} />
        <Route path="/terminal/:slug" element={<Terminal />} />
        <Route path="/vote-results" element={<PrivateRoute><VoteResults /></PrivateRoute>} />
        <Route path="/link-accounts" element={<PrivateRoute><LinkAccounts /></PrivateRoute>} />
        <Route path="/general-play" element={<PrivateRoute><GeneralPlay /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/finance" element={<PrivateRoute><Finance /></PrivateRoute>} />
        <Route path="/twos" element={<PrivateRoute><TwosAndFines /></PrivateRoute>} />
        <Route path="/winners" element={<PrivateRoute><WinnersAndLosers /></PrivateRoute>} />
        <Route path="/multiarchive" element={<PrivateRoute><MultiArchive /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><Players /></PrivateRoute>} />
        <Route path="/player/login" element={<PlayerLogin />} />
        {/* Short alias so the Player Portal login has a URL worth typing/sharing/putting
            on a QR code, without renaming the whole /player/ namespace used everywhere
            else (register, dashboard, results, etc.) — same component, same behaviour. */}
        <Route path="/plogin" element={<PlayerLogin />} />
        <Route path="/player/register" element={<PlayerRegister />} />
        <Route path="/player/dashboard" element={<PlayerDashboard />} />
        <Route path="/player/verify/:player_id/:event_id" element={<PlayerVerify />} />
        <Route path="/player/hcp-verify" element={<PlayerHcpVerify />} />
        <Route path="/player/declare-marker" element={<PlayerDeclareMarker />} />
        <Route path="/player/change-password" element={<PlayerChangePassword />} />
        <Route path="/player/my-results" element={<PlayerMyResults />} />
        <Route path="/player/forgot-password" element={<PlayerForgotPassword />} />
        <Route path="/player/reset-password" element={<PlayerResetPassword />} />
        <Route path="/verify" element={<VerifyCard />} />
        <Route path="/tuesday" element={<TuesdayGolf />} />
        <Route path="/vote" element={<EventAttendance />} />
        {/* Friendly, brandable link for Pin High members — bounces straight
            into the Player Portal (dashboard if already signed in, login
            otherwise) instead of the plain /player/login URL. */}
        <Route path="/pinhighplayers" element={<Navigate to="/player/dashboard" />} />
        <Route path="/" element={window.location.hostname.includes('tuesday') || window.location.hostname.includes('tues') ? <Navigate to="/tuesday" /> : <Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}
export default App;
